import { TEntityMilestone, TFeatureWorkflowInstance } from "entity-app/models";
import { InfoCircleOutlined } from "@ant-design/icons";

import { DateUtils } from "utils/dateutils";
import { Moment } from "moment";
import { Button, Tooltip } from "antd";
import { useMemo, useRef, useState } from "react";
import DatePickerWithIcon from "components/date-picker-with-icon/date-picker-with-icon";
import { getDaysText } from "utils/utils";
import SetActualDatePicker from "components/date-block/set-actual-date-button";
import TimeEarlyStartIcon from "components/svg-icons/time-early-start";
import { ErrorMessages, EWorkflowStatusDataBlock } from "constants/index";
import EarlyStartModelConfirm from "popups/time-early-start-model";
import { FeatureTypes } from "entity-app/constants";
import { TDateBlockPermissions, TWidthCalculation } from "../../models";

const canShowCurrentDelay = (inputDateBlock: TFeatureWorkflowInstance) => {
  return (
    inputDateBlock?.calculation_details?.projecting_entity
      ?.delay_in_forwarded_date !== true
  );
};

function MileStoneBlock(props: {
  milestone: TEntityMilestone;
  prevMilestone: TEntityMilestone | undefined;
  nextMilestone: TEntityMilestone | undefined;
  currentWorkflowMilestone: TEntityMilestone | undefined;
  widthCal: TWidthCalculation;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  applyWorkflowChange: (data: any) => Promise<void>;
  governingTaskDiv: JSX.Element | null;
  permissions: TDateBlockPermissions;
}) {
  const {
    milestone,
    widthCal,
    prevMilestone,
    nextMilestone,
    currentWorkflowMilestone,
    featureWorkflowInstance,
    applyWorkflowChange,
    governingTaskDiv,
    permissions
  } = props;

  const [isOpenEarlyStartModel, setIsOpenEarlyStartModel] = useState(false);

  const canShowCurrentDelayPermission = useMemo(
    () => canShowCurrentDelay(featureWorkflowInstance),
    [featureWorkflowInstance]
  );

  const infoMilestoneAction = (
    <Tooltip title={milestone.milestone_action}>
      <InfoCircleOutlined className="pl-1 text-gray-50 hover:text-white " />
    </Tooltip>
  );

  const disabledActualDate = (current: Moment) => {
    const after = DateUtils.dateTimeObj(current).isAfter(
      DateUtils.dateTimeObj()
    );
    if (!prevMilestone) return after;

    const currDateFromPicker = DateUtils.dateTimeObj(current)
      .hours(13)
      .minutes(0)
      .seconds(0);
    const before = currDateFromPicker.isBefore(
      DateUtils.dateTimeObj(prevMilestone?.actual_date)
    );
    return before || after;
  };

  const childRefPlannedDate = useRef<any>();
  const renderExtraFooterDiv = (
    <div className="flex justify-center py-2">
      <Button
        // disabled={cannotChangePlannedDate}
        onClick={() => {
          childRefPlannedDate.current?.close();

          const set = {
            milestones: [{ actual_date: null, id: milestone.id }]
          };
          applyWorkflowChange(set);
        }}
        data-testid="index-button-clear-date"
      >
        Clear Date
      </Button>
    </div>
  );

  const onChangeActualDate = (date: Moment | null) => {
    if (date) {
      const saveDate = DateUtils.formatDateWithLunchTime(date);
      const set = {
        milestones: [{ actual_date: saveDate, id: milestone.id }]
      };
      applyWorkflowChange(set);
    }
  };

  const actualDatePicker = (
    <DatePickerWithIcon
      value={
        milestone.actual_date
          ? DateUtils.dateTimeObj(milestone.actual_date)
          : undefined
      }
      onChange={onChangeActualDate}
      ref={childRefPlannedDate}
      disabledDate={disabledActualDate}
      renderExtraFooter={() => {
        return milestone.actual_date ? renderExtraFooterDiv : undefined;
      }}
    />
  );

  const canEditProjectedDate =
    featureWorkflowInstance.workflow_status ===
      EWorkflowStatusDataBlock.IN_PROGRESS && milestone.sequence_no !== 1;
  const disabledProjectedDate = (current: Moment) => {
    const currDateFromPicker = DateUtils.dateTimeObj(current)
      .hours(13)
      .minutes(23)
      .seconds(24);

    const todayDate = DateUtils.dateTimeObj().hours(13).minutes(23).seconds(23);

    const beforeToday = currDateFromPicker.isBefore(todayDate);
    const dependentDate =
      prevMilestone?.actual_date || prevMilestone?.projected_date;
    const beforePrevMilestoneActualDate = dependentDate
      ? currDateFromPicker.isBefore(DateUtils.dateTimeObj(dependentDate))
      : false;

    return beforeToday || beforePrevMilestoneActualDate;
  };

  const projectedDiv = !milestone.actual_date && milestone.projected_date && (
    <div className="flex-1 pt-2">
      <div className="flex items-center">
        <span className="font-semibold mr-1">Projected date:</span>
        {canEditProjectedDate && (
          <DatePickerWithIcon
            value={
              milestone.projected_date
                ? DateUtils.dateTimeObj(milestone.projected_date)
                : undefined
            }
            disabled={!permissions.canEditProjectedDate}
            disabledDate={disabledProjectedDate}
            onChange={(date) => {
              if (date) {
                const saveDate = date
                  ? DateUtils.formatDateWithLunchTime(date)
                  : null;
                const set = {
                  milestones: [{ projected_date: saveDate, id: milestone.id }]
                };
                applyWorkflowChange(set);
              }
            }}
          />
        )}
      </div>
      <span
        className={
          canShowCurrentDelayPermission &&
          featureWorkflowInstance.current_delay != null &&
          featureWorkflowInstance.current_delay > 0 &&
          currentWorkflowMilestone !== milestone
            ? "bg-delay pl-1 pr-2 py-[1px] rounded italic"
            : ""
        }
      >
        {DateUtils.format(milestone.projected_date)}
      </span>
    </div>
  );

  const plannedDiv = (
    <div className="flex-1 flex-col text-block-card space-y-1">
      <div className="font-semibold">Planned date: </div>
      <span>
        {milestone.planned_date
          ? DateUtils.format(milestone.planned_date)
          : "-"}
      </span>
    </div>
  );

  const canEditActualDate =
    (currentWorkflowMilestone === milestone ||
      currentWorkflowMilestone === nextMilestone) &&
    permissions.canEditActualDate;

  const dash = milestone.projected_date ? "" : "-";

  const { arePredecessorsComplete } = permissions;

  const isEarlyStart =
    (permissions.isDependedEntityInProgress || !arePredecessorsComplete) &&
    featureWorkflowInstance.feature_type_id === FeatureTypes.MATERIALS &&
    milestone.sequence_no === 1;

  const materialReleasedEarlyMsg = useMemo(() => {
    return (
      <div className="p-2">
        <div className="mb-1.5">Material Released Early.</div>
        <div>
          <span className="font-semibold">Reason Cited:</span>{" "}
          <span>{featureWorkflowInstance.wf_override_reason}</span>
        </div>
      </div>
    );
  }, [featureWorkflowInstance.wf_override_reason]);

  const earlyStartActualDiv = (
    <div className="flex-1 flex-col text-block-card space-y-1">
      <div className="flex space-x-1 items-center">
        <div className="font-semibold">Actual date:</div>
        <Tooltip
          title={
            featureWorkflowInstance.wf_override_reason
              ? materialReleasedEarlyMsg
              : ErrorMessages.EarlyStartMsg
          }
        >
          <div className="min-w-0 pl-1 items-center flex">
            <TimeEarlyStartIcon
              className={
                canEditActualDate
                  ? "hover:opacity-100 opacity-80 cursor-pointer"
                  : "opacity-50 cursor-not-allowed"
              }
              onClick={() => {
                setIsOpenEarlyStartModel(true);
              }}
            />
          </div>
        </Tooltip>
      </div>

      <span>
        {milestone.actual_date ? DateUtils.format(milestone.actual_date) : dash}
      </span>
    </div>
  );

  const actualDiv = !milestone.actual_date ? (
    <div>
      {canEditActualDate && (
        <div className="absolute bottom-2 left-[13px]">
          <SetActualDatePicker
            onChange={onChangeActualDate}
            disabledDate={disabledActualDate}
          />
        </div>
      )}
    </div>
  ) : (
    <div className="flex-1 flex-col text-block-card space-y-1">
      <div className="flex space-x-1 items-center">
        <div className="font-semibold">Actual date:</div>
        <div>{canEditActualDate && actualDatePicker}</div>
      </div>

      <span>
        {milestone.actual_date ? DateUtils.format(milestone.actual_date) : dash}
      </span>
    </div>
  );

  const classNameBlock = useMemo(() => {
    const blockClass =
      "shrink-0 border border-solid text-xs h-48 relative rounded";
    const headerClass = "card-header";

    if (currentWorkflowMilestone === milestone) {
      if (!milestone.planned_date)
        return {
          blockClass: `${blockClass} border-ciq-primary-900 border-2`,
          headerClass: `${headerClass} !bg-ciq-primary-900 border-b-0 !text-white`
        };

      const isPlannedDateIsDelay =
        DateUtils.dateTimeObj()
          .startOf("D")
          .diff(
            DateUtils.dateTimeObj(milestone.planned_date).startOf("D"),
            "d"
          ) > 0;
      return isPlannedDateIsDelay
        ? {
            blockClass: `${blockClass} next-milestone-delay-border`,
            headerClass: `${headerClass} next-milestone-delay-text`
          }
        : {
            blockClass: `${blockClass} next-milestone-on-track-border`,
            headerClass: `${headerClass} next-milestone-on-track-text`
          };
    }
    return { blockClass, headerClass };
  }, [currentWorkflowMilestone, milestone]);

  const currentDelayDiv = (
    <div className="h-12 w-full">
      {canShowCurrentDelayPermission &&
        featureWorkflowInstance.current_delay != null &&
        featureWorkflowInstance.current_delay > 0 &&
        currentWorkflowMilestone === milestone && (
          <div className="bg-delay text-xs p-1 flex items-center rounded-b">
            <div className="grow">
              <div className="flex items-center justify-center lowercase">
                <span className="font-bold pr-1">
                  {featureWorkflowInstance.current_delay}{" "}
                  {getDaysText(featureWorkflowInstance.current_delay)}
                </span>
                <span>late</span>
              </div>
            </div>
            <Tooltip
              title={`As of today, ${DateUtils.format(
                featureWorkflowInstance.today_date
              )}.`}
              overlayInnerStyle={{ color: "#000000CC" }}
            >
              <InfoCircleOutlined />
            </Tooltip>
          </div>
        )}
      {!nextMilestone && governingTaskDiv}
    </div>
  );

  return (
    <div
      style={{ width: widthCal.milestonesWidth }}
      data-testid={`timeline-milestones-milestoneblock-${milestone.id}`}
    >
      <div
        className={classNameBlock.blockClass}
        data-testid={`timeline-milestones-block-${milestone.id}`}
      >
        <div
          className={classNameBlock.headerClass}
          data-testid={`timeline-milestones-header-${milestone.id}`}
        >
          <Tooltip title={milestone.name}>
            <div
              className="ellipsis-clamp-2"
              data-testid={`timeline-milestones-title-${milestone.id}`}
            >
              {milestone.name}
            </div>
          </Tooltip>

          {currentWorkflowMilestone === milestone && infoMilestoneAction}
        </div>
        <div
          className="p-3"
          data-testid={`timeline-milestones-content-${milestone.id}`}
        >
          <div className="text-xs h-full space-y-3">
            {plannedDiv}
            {projectedDiv}
            {isEarlyStart ? earlyStartActualDiv : actualDiv}
          </div>
        </div>
      </div>
      {currentDelayDiv}
      {isOpenEarlyStartModel && (
        <EarlyStartModelConfirm
          isModalOpen={isOpenEarlyStartModel}
          setIsModalOpen={setIsOpenEarlyStartModel}
          milestoneName={milestone.name}
          existingActualDate={milestone.actual_date}
          existingReason={featureWorkflowInstance.wf_override_reason || null}
          onOk={(prop) => {
            const saveDate = prop.actualDate
              ? DateUtils.formatDateWithLunchTime(prop.actualDate)
              : null;

            const set = {
              wf_override_reason: prop.reason,
              milestones: [
                {
                  actual_date: saveDate,
                  id: milestone.id
                }
              ]
            };
            return applyWorkflowChange(set);
          }}
          disabledDate={disabledActualDate}
          data-testid={`timeline-milestones-earlystart-confirm-${milestone.id}`}
        />
      )}
    </div>
  );
}

export default function TimelineMilestones(props: {
  milestones: Array<TEntityMilestone>;
  currentMilestone: TEntityMilestone | undefined;
  featureWorkflowInstance: TFeatureWorkflowInstance;
  widthCal: TWidthCalculation;
  applyWorkflowChange: (data: any) => Promise<void>;
  governingTaskDiv: JSX.Element | null;
  permissions: TDateBlockPermissions;
}) {
  const {
    milestones,
    currentMilestone,
    widthCal,
    featureWorkflowInstance,
    applyWorkflowChange,
    governingTaskDiv,
    permissions
  } = props;
  const { length } = milestones;

  return (
    <div
      className="flex h-[200px] space-x-6"
      data-testid="timeline-milestones-root"
    >
      {milestones.map((m, index) => {
        const prev = index > 0 ? milestones[index - 1] : undefined;
        const next = index < length - 1 ? milestones[index + 1] : undefined;
        return (
          <MileStoneBlock
            milestone={m}
            widthCal={widthCal}
            prevMilestone={prev}
            nextMilestone={next}
            currentWorkflowMilestone={currentMilestone}
            featureWorkflowInstance={featureWorkflowInstance}
            key={`TimelineMilestone_${m.id}`}
            applyWorkflowChange={applyWorkflowChange}
            governingTaskDiv={governingTaskDiv}
            permissions={permissions}
            data-testid={`timeline-milestones-milestoneblock-wrapper-${m.id}`}
          />
        );
      })}
    </div>
  );
}
